import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import {
  collection,
  addDoc,
  doc,
  getDocs,
  updateDoc,
  deleteDoc,
  Timestamp
} from 'firebase/firestore';
import Modal from 'react-modal';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';

Modal.setAppElement('#root');

const SalaryStructure = () => {
  const { user, userData } = useUserAuth();
  const [update, setUpdate] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [structures, setStructures] = useState([]);
  const [filteredStructures, setFilteredStructures] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [newStructure, setNewStructure] = useState({
    name: '',
    type: 'addition',              // addition or subtraction
    calculation: 'fixed',          // fixed or percentage
    percentageOf: 'basic'          // basic or gross (only if percentage)
  });

  const [editingStructure, setEditingStructure] = useState(null);
  const [currentStructure, setCurrentStructure] = useState(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isFetching, setIsFetching] = useState(true);

  const salaryStructureCollectionRef = collection(db, "salaryStructure");

  useEffect(() => {
    fetchSalaryStructures();
  }, []);

  useEffect(() => {
    const filtered = structures.filter((item) =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.calculation && item.calculation.includes(searchTerm.toLowerCase()))
    );
    setFilteredStructures(filtered);
  }, [searchTerm, structures]);

  const fetchSalaryStructures = async () => {
    setIsFetching(true);
    try {
      const data = await getDocs(salaryStructureCollectionRef);
      const list = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      setStructures(list);
    } catch (error) {
      setError("Failed to load salary structure");
      setShowModal(true);
    } finally {
      setIsFetching(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewStructure(prev => ({ ...prev, [name]: value }));
  };

  const handleAddStructure = async () => {
    if (!newStructure.name.trim()) {
      setError("Component name is required");
      setShowModal(true);
      return;
    }

    setUpdate(true);
    try {
      await addDoc(salaryStructureCollectionRef, {
        ...newStructure,
        createdAt: Timestamp.now()
      });
      setSuccessMessage("Salary component added successfully");
      fetchSalaryStructures();
      setNewStructure({
        name: '',
        type: 'addition',
        calculation: 'fixed',
        percentageOf: 'basic'
      });
      setIsAddModalOpen(false);
    } catch (error) {
      setError("Failed to add component");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleUpdateStructure = async () => {
    setUpdate(true);
    try {
      const docRef = doc(db, "salaryStructure", editingStructure.id);
      await updateDoc(docRef, {
        name: newStructure.name,
        type: newStructure.type,
        calculation: newStructure.calculation,
        percentageOf: newStructure.percentageOf
      });
      setSuccessMessage("Component updated successfully");
      fetchSalaryStructures();
      setIsEditModalOpen(false);
      setEditingStructure(null);
    } catch (error) {
      setError("Failed to update component");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleEdit = (item) => {
    setEditingStructure(item);
    setNewStructure({
      name: item.name,
      type: item.type,
      calculation: item.calculation || 'fixed',
      percentageOf: item.percentageOf || 'basic'
    });
    setIsEditModalOpen(true);
  };

  const confirmDelete = (item) => {
    setCurrentStructure(item);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    setUpdate(true);
    try {
      await deleteDoc(doc(db, "salaryStructure", currentStructure.id));
      setSuccessMessage("Component deleted successfully");
      fetchSalaryStructures();
      setIsDeleteModalOpen(false);
    } catch (error) {
      setError("Failed to delete component");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  if (!userData) return <Loader text="READING" />;

  const allowedRoles = ['Admin', 'Supreme'];

  return (
    <>
      {update && <Loader text="PROCESSING" />}

      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <h2 className="manage-page-heading caps size4 margin-top-cashflow1">Salary Structure</h2>
            <Link to="#" className="home-link" onClick={(e) => { e.preventDefault(); fetchSalaryStructures(); }}>
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>

          <div className="login-page-items login-page-items-cashflow column align-center scroll-fixed scroll-fixed6">
            <button className="login-button login-button-d caps" onClick={() => setIsAddModalOpen(true)}>
              Add Component
            </button>

            <input
              type="text"
              className="input-field1"
              placeholder="Search components..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <div className="scroll-fixed scroll-fixed1">
              <div className="class-list class-list-d scrollable">

                {isFetching ? (
                  <p className="loading-text">Loading salary components...</p>
                ) : filteredStructures.length === 0 ? (
                  <p className="loading-text">No components found.</p>
                ) : (
                  <>
                    {/* Additions */}
                    {filteredStructures.filter(s => s.type === 'addition').length > 0 && (
                      <div className="mb-20">
                        <h3 className="size5 caps">Additions (Allowances)</h3>
                        {filteredStructures
                          .filter(s => s.type === 'addition')
                          .map((item) => (
                            <div key={item.id} className="class-item">
                              <div>
                                <p>
                                    <strong>{item.name}</strong><br />
                                    <small>
                                    {item.calculation === 'percentage'
                                        ? `${item.percentageOf === 'gross' ? 'Gross Pay' : 'Basic Salary'} Percentage`
                                        : 'Fixed Amount'}
                                    </small>
                                </p>                                
                              </div>
                              <div className="row align-center justify-center gap-15">
                                <button className="list-btn" onClick={() => handleEdit(item)}>
                                  <i className="fas fa-edit"></i>
                                </button>
                                <button className="list-btn delete-btn" onClick={() => confirmDelete(item)}>
                                  <i className="fas fa-trash-alt"></i>
                                </button>
                              </div>
                            </div>
                          ))}
                      </div>
                    )}

                    {/* Deductions */}
                    {filteredStructures.filter(s => s.type === 'subtraction').length > 0 && (
                      <div className="mb-20">
                        <h3 className="size5 caps">Deductions</h3>
                        {filteredStructures
                          .filter(s => s.type === 'subtraction')
                          .map((item) => (
                            <div key={item.id} className="class-item">
                              <div>
                                <p>
                                    <strong>{item.name}</strong><br />
                                    <small>
                                    {item.calculation === 'percentage'
                                        ? `${item.percentageOf === 'gross' ? 'Gross Pay' : 'Basic Salary'} Percentage`
                                        : 'Fixed Amount'}
                                    </small>
                                </p>                                
                              </div>
                              <div className="row align-center justify-center gap-15">
                                <button className="list-btn" onClick={() => handleEdit(item)}>
                                  <i className="fas fa-edit"></i>
                                </button>
                                <button className="list-btn delete-btn" onClick={() => confirmDelete(item)}>
                                  <i className="fas fa-trash-alt"></i>
                                </button>
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}

      {/* Success/Error Modal */}
      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => { setError(""); setSuccessMessage(""); setShowModal(false); }}
        />
      )}

      {/* Add Modal */}
      <Modal isOpen={isAddModalOpen} onRequestClose={() => setIsAddModalOpen(false)} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Add Salary Component</h2>
        <form className="session-form modal-scroll">
          <label className="input-label">Name</label>
          <input
            type="text"
            name="name"
            className="input-field"
            placeholder="e.g. Housing Allowance, Tax, Pension"
            value={newStructure.name}
            onChange={handleInputChange}
          />
          <label className="input-label">Addition/Deduction</label>
          <select name="type" className="input-field mt-10" value={newStructure.type} onChange={handleInputChange}>
            <option value="addition">Addition (Allowance)</option>
            <option value="subtraction">Deduction</option>
          </select>
          <label className="input-label">Percentage</label>
          <select name="calculation" className="input-field mt-10" value={newStructure.calculation} onChange={handleInputChange}>
            <option value="fixed">Fixed Amount</option>
            <option value="percentage">Percentage</option>
          </select>
          
          {newStructure.calculation === 'percentage' && (
            <>
              <label className="input-label">Percentage of</label>
              <select name="percentageOf" className="input-field mt-10" value={newStructure.percentageOf} onChange={handleInputChange}>
                <option value="basic">Percentage of Basic Salary</option>
                <option value="gross">Percentage of Gross Pay</option>
              </select>
            </>
          )}

          <div className="row align-center gap-1 mt-20">
            <button className="login-button" type="button" onClick={() => setIsAddModalOpen(false)}>Cancel</button>
            <button className="login-button" type="button" onClick={handleAddStructure}>Add Component</button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={isEditModalOpen} onRequestClose={() => { setIsEditModalOpen(false); setEditingStructure(null); }} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Edit Salary Component</h2>
        <form className="session-form modl-scroll">
          <label className="input-label">Name</label>
          <input
            type="text"
            name="name"
            className="input-field"
            placeholder="Component name"
            value={newStructure.name}
            onChange={handleInputChange}
          />
          <label className="input-label">Addition/Deduction</label>
          <select name="type" className="input-field mt-10" value={newStructure.type} onChange={handleInputChange}>
            <option value="addition">Addition (Allowance)</option>
            <option value="subtraction">Deduction</option>
          </select>
          <label className="input-label">Percentage</label>            
          <select name="calculation" className="input-field mt-10" value={newStructure.calculation} onChange={handleInputChange}>
            <option value="fixed">Fixed Amount</option>
            <option value="percentage">Percentage</option>
          </select>
          
          {newStructure.calculation === 'percentage' && (
            <>
              <label className="input-label">Percentage of</label>
              <select name="percentageOf" className="input-field mt-10" value={newStructure.percentageOf} onChange={handleInputChange}>
                <option value="basic">Percentage of Basic Salary</option>
                <option value="gross">Percentage of Gross Pay</option>
              </select>
            </>
          )}

          <div className="row align-center justify-center gap-1 mt-20">
            <button className="login-button" type="button" onClick={() => { setIsEditModalOpen(false); setEditingStructure(null); }}>Cancel</button>
            <button className="login-button" type="button" onClick={handleUpdateStructure}>Update</button>
          </div>
        </form>
      </Modal>

      {/* Delete Modal */}
      <Modal isOpen={isDeleteModalOpen} onRequestClose={() => setIsDeleteModalOpen(false)} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Delete Component</h2>
        {currentStructure && (
          <div>
            <p>Delete <strong>{currentStructure.name}</strong> permanently?</p>
            <p>This action cannot be undone.</p>
            <div className="row align-center gap-1">
              <button className="login-button" type="button" onClick={() => setIsDeleteModalOpen(false)}>Cancel</button>
              <button className="login-button delete-btn" type="button" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default SalaryStructure;